import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDiceSubscriptions } from '../../hooks/useApi';
import { trackEvent } from '../../services/analytics';
import { Button } from '../../components/base-components';
import { PageShell } from '../components/PageShell';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { Skeleton } from '../components/Skeleton';
import { v2Path } from '../nav';

/* ------------------------------------------------------------------ *
 * Logic block copied verbatim from src/pages/DiceSuccessPage.tsx —
 * the 3s poll, the 60s timeout, the done/timed-out/missing branches.
 * Only the JSX and the className strings are new, plus the three internal
 * paths which now route through v2Path().
 * ------------------------------------------------------------------ */

const POLL_TIMEOUT_MS = 60_000; // Stop polling after 60s

export const DiceSuccessScreen: React.FC = () => {
  const navigate = useNavigate();
  const { data: subData, isLoading, refetch } = useDiceSubscriptions();

  useEffect(() => { trackEvent('dice_success_page_viewed'); }, []);

  const storedSubId = sessionStorage.getItem('dice_subscription_id');
  const subs = subData?.subscriptions || [];

  // Find the subscription we just paid for
  const targetSub = storedSubId
    ? subs.find((s) => String(s.id) === storedSubId)
    : subs.find((s) => s.is_active);

  const isSettingUp = targetSub && !targetSub.connection_id;
  const isDone = targetSub && !!targetSub.connection_id;

  // Track whether polling has timed out
  const [timedOut, setTimedOut] = useState(false);
  const pollStartRef = useRef<number | null>(null);

  // Poll every 3s while setup is in progress, stop after 60s
  useEffect(() => {
    if (!isSettingUp || timedOut) return;

    if (!pollStartRef.current) {
      pollStartRef.current = Date.now();
    }

    const interval = setInterval(() => {
      if (Date.now() - pollStartRef.current! > POLL_TIMEOUT_MS) {
        setTimedOut(true);
        clearInterval(interval);
        return;
      }
      refetch();
    }, 3000);

    return () => clearInterval(interval);
  }, [isSettingUp, timedOut, refetch]);

  // Stop polling once done
  useEffect(() => {
    if (isDone) {
      trackEvent('dice_setup_completed', { subscriptionId: storedSubId || '' });
      setTimedOut(false);
      pollStartRef.current = null;
      if (storedSubId) {
        sessionStorage.removeItem('dice_subscription_id');
      }
    }
  }, [isDone, storedSubId]);

  const breadcrumb = [
    { label: 'Dice Accounts', to: v2Path('/dice/settings') },
    { label: 'Setting up' },
  ];

  if (isLoading) {
    return (
      <PageShell width="narrow">
        <PageHeader
          title="Setting Up Your Dice Account"
          subtitle="Payment received — your account is being created automatically."
          breadcrumb={breadcrumb}
        />
        <div className="space-y-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-32 w-full" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell width="narrow">
      <PageHeader
        title={isDone ? "You're all set!" : 'Setting Up Your Dice Account'}
        subtitle={
          isDone
            ? 'Your Dice account has been created and linked.'
            : 'Payment received — your account is being created automatically.'
        }
        breadcrumb={breadcrumb}
      />

      {/* Setting up — auto-completing in the background */}
      {isSettingUp && !timedOut && (
        <Panel>
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#3b82f6] border-t-transparent" />
            <p className="text-sm text-white/70">Creating your Dice account...</p>
            <p className="text-sm text-white/50">
              {targetSub.phone_number
                ? `Assigned number: ${targetSub.phone_number} — waiting for verification to complete.`
                : 'Assigning a phone number and requesting verification...'}
            </p>
            <p className="text-xs text-white/40">
              This usually takes 10-30 seconds. This page will update automatically.
            </p>
          </div>
        </Panel>
      )}

      {/* Timed out — setup didn't finish */}
      {isSettingUp && timedOut && (
        <Panel>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-white/70">
              Setup is taking longer than expected. Your account may still be processing in the
              background.
            </p>
            <p className="text-sm text-white/50">
              {targetSub.phone_number
                ? `Number assigned: ${targetSub.phone_number}`
                : 'No number assigned yet.'}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => { setTimedOut(false); pollStartRef.current = Date.now(); refetch(); }}
              >
                Keep Waiting
              </Button>
              <Button variant="secondary" onClick={() => navigate(v2Path('/dice/settings'))}>
                Go to Settings
              </Button>
            </div>
          </div>
        </Panel>
      )}

      {/* Done */}
      {isDone && (
        <Panel>
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div className="min-w-0">
                <p className="text-xs text-white/50">Phone Number</p>
                <p className="break-all text-sm text-white/70">{targetSub.phone_number || '—'}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-white/50">Dice Account Email</p>
                <p className="break-all text-sm text-white/70">{targetSub.connection_email || '—'}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-white/50">Forwarding Email</p>
                <p className="break-all text-sm text-white/70">{targetSub.forwarding_email}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-white/50">Status</p>
                <span className="text-sm text-green-400">Active</span>
              </div>
            </div>
            <p className="text-sm text-white/50">
              All SMS messages will be forwarded to {targetSub.forwarding_email}.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate(v2Path('/dice/settings'))}>
                Manage Accounts
              </Button>
              <Button variant="secondary" onClick={() => navigate(v2Path('/accounts'))}>
                Go to Accounts
              </Button>
            </div>
          </div>
        </Panel>
      )}

      {/* No subscription found */}
      {!targetSub && !isLoading && (
        <Panel>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-white/70">
              Could not find your subscription. It may still be processing.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => refetch()}>Refresh</Button>
              <Button variant="secondary" onClick={() => navigate(v2Path('/dice/signup'))}>
                Back to Signup
              </Button>
            </div>
          </div>
        </Panel>
      )}
    </PageShell>
  );
};

export default DiceSuccessScreen;
